import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import TickerTimeout from "../../../utils/TickerTimeout";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";

/**
 * Controls tension spine animations for individual reels
 */
class TensionReelController {
  // Track which reels currently have active anticipation sound.
  // Sound starts when the first reel enters tension, stops when the last exits.
  static _activeSoundReels = new Set();
  static _pendingSoundStop = null; // TickerTimeout for delayed sound stop

  // ── Anticipation Sound Helpers ──────────────────────────────────────────

  /**
   * Register this reel as having active tension sound.
   * Starts the loop sound if this is the first reel.
   */
  _startAnticipationSound() {
    // Cancel any pending delayed stop — a new reel just entered tension
    if (TensionReelController._pendingSoundStop) {
      TensionReelController._pendingSoundStop.clear();
      TensionReelController._pendingSoundStop = null;
    }
    const wasEmpty = TensionReelController._activeSoundReels.size === 0;
    TensionReelController._activeSoundReels.add(this.reelIndex);
    if (wasEmpty) {
      soundManager.play(SOUND_NAME.main_anticipation_loop);
    }
  }

  /**
   * Unregister this reel from active tension sound.
   * Stops the loop sound (with a short delay) only when the last reel exits.
   */
  _stopAnticipationSound() {
    TensionReelController._activeSoundReels.delete(this.reelIndex);
    if (TensionReelController._activeSoundReels.size === 0) {
      // Let the loop linger briefly so it doesn't cut abruptly
      TensionReelController._pendingSoundStop = new TickerTimeout(() => {
        soundManager.stop(SOUND_NAME.main_anticipation_loop);
        TensionReelController._pendingSoundStop = null;
      }, 175);
    }
  }

  constructor(reelContainer, reelIndex, symbolWidth, symbolHeight) {
    this.reelContainer = reelContainer;
    // All reels use the same tension spine — side reels use different animation names
    const isSideReel = reelIndex === 0 || reelIndex === Settings.REEL_COUNT - 1;
    this.tensionSpineData = {
      skeleton: "anticipation-skeleton",
      atlas: "anticipation-atlas",
    };
    this.isMirrored = reelIndex === 0; // First reel mirrors the side spine

    // Animation name mapping: side reels use side_in/side_idle/side_out
    this.animNames = isSideReel
      ? { in: "side_in", idle: "side_idle", out: "side_out" }
      : { in: "in", idle: "idle", out: "out" };
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.tensionSpine = null;
    this.isVisible = false;
    this.initialized = false;

    // Spine scaling configuration
    this.widthMultiplier = 0.92;  // Width reduction for pixel-perfect fit
    this.heightMultiplier = 0.99; // Slight height reduction for pixel-perfect fit
    this.yOffsetMultiplier = 0; // No offset — spine positioned as designed

    // Track active TickerTimeouts for cleanup
    this.currentTimeouts = [];

    // Internal caches for performance optimization
    this._spineScaleCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;
    this._availableAnimationsCache = null;
    this._tensionOutDurationCache = null;
    this._tensionCycleDurationCache = null;

    this.initializeTensionSpine();
  }

  /**
   * Calculate responsive scale and positioning for spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height
   * @returns {Object} - Scale and positioning information
   * Cached for performance - invalidated when dimensions change
   */
  calculateSpineScale(spineInstance, containerWidth, containerHeight) {
    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._spineScaleCache &&
      this._spineScaleCache.cacheKey === cacheKey &&
      this._lastSymbolWidth === containerWidth &&
      this._lastSymbolHeight === containerHeight
    ) {
      return this._spineScaleCache.result;
    }

    // Set default animation momentarily to get proper bounds, then clear it
    // so we don't leave a looping animation running (wastes CPU, interferes with warmup)
    if (
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, false);
      spineInstance.state.update(0);
      spineInstance.state.apply(spineInstance.skeleton);
      spineInstance.state.clearTracks();
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache = {
      cacheKey,
      result,
    };
    this._lastSymbolWidth = containerWidth;
    this._lastSymbolHeight = containerHeight;

    return result;
  }

  /**
   * Hide tension animation immediately
   */
  hideTension() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      this.isVisible = false;
      this._stopAnticipationSound();
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        this.isVisible = false;
        this._stopAnticipationSound();
        return;
      }

      this._stopAnticipationSound();
      this.tensionSpine.alpha = 0.001;
      this.tensionSpine.autoUpdate = false;
      this.isVisible = false;

      if (this.tensionSpine.state) {
        try {
          this.tensionSpine.state.clearListeners();
          this.tensionSpine.state.clearTracks();
        } catch (error) {
          // Clear tracks error
        }
      }
      // NOTE: Do NOT call setToSetupPose() here.
      // The setup pose shows the tension fully "open" — if playTensionIn fires
      // before the next tick, that pose flashes for one frame at alpha=1.
      // clearTracks() is sufficient cleanup; the spine is invisible at alpha=0.001.
    });
  }

  /**
   * Force stop tension animations with smooth exit transition
   */
  forceStopTension() {
    const currentAnimation = this.tensionSpine?.state?.getCurrent(0);
    this.clearAllTimeouts();

    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state ||
      !this.getAvailableAnimations().includes(this.animNames.out)
    ) {
      this.hideTension();
      return;
    }

    // If tension_out is already playing, don't restart it
    if (currentAnimation && currentAnimation.animation.name === this.animNames.out) {

      return;
    }

    const isPlayingTension =
      currentAnimation &&
      (currentAnimation.animation.name === this.animNames.in ||
        currentAnimation.animation.name === this.animNames.idle);

    if (isPlayingTension) {
      this.tensionSpine.state.setAnimation(0, this.animNames.out, false);

      const animationDuration =
        this.tensionSpine.skeleton.data.animations.find(
          (anim) => anim.name === this.animNames.out
        )?.duration || 1.0;

      const timeoutId = new TickerTimeout(() => {
        this.hideTension();
      }, animationDuration * 1000);

      this.currentTimeouts.push(timeoutId);
    } else {
      this.hideTension();
    }
  }

  /**
   * Clear all active timeouts
   */
  clearAllTimeouts() {
    for (const t of this.currentTimeouts) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    this.currentTimeouts = [];
  }

  // Pause/resume for tab switching — NO-OPS.
  // Spine uses autoUpdate=true which is driven by PIXI.Ticker.shared.
  // When AppActivityContext sets ticker.speed=0, the spine freezes automatically.
  // TickerTimeout hide-timers also freeze automatically.
  // Methods kept for API compatibility with callers.
  pauseTension() {}
  resumeTension() {}

  /**
   * Check if tension is currently visible and playing
   * Returns true if tension is visible and should not be restarted
   * @returns {boolean} True if tension is visible and active
   */
  isTensionVisible() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return false;
    }

    // Check if spine is visible (alpha > 0.5 means it's showing)
    if (this.tensionSpine.alpha <= 0.5) {
      return false;
    }

    // Check if currently playing tension animations
    const currentAnimation = this.tensionSpine.state.getCurrent(0);
    if (!currentAnimation) {
      return false;
    }

    const animationName = currentAnimation.animation.name;
    const isVisible = (
      animationName === this.animNames.in ||
      animationName === this.animNames.idle ||
      animationName === this.animNames.out  // Include tension_out - it's still visible!
    );

    return isVisible;
  }


  /**
   * Play tension intro animation and transition to idle loop
   */
  playTensionIn() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    try {
      const currentAnim = this.tensionSpine.state.getCurrent(0);
      const isWarmupState =
        currentAnim &&
        currentAnim.animation.name === this.animNames.in &&
        this.tensionSpine.state.timeScale === 0;

      this.tensionSpine.state.clearListeners();

      if (isWarmupState) {
        // Fast path: warmup already set up tension_in paused at frame 0 — just unpause
        this.tensionSpine.state.timeScale = 1;
        this.tensionSpine.autoUpdate = true;
        this.isVisible = true;

        const animations = this.getAvailableAnimations();
        if (animations.includes(this.animNames.idle)) {
          this.tensionSpine.state.addAnimation(0, this.animNames.idle, true, 0);
        }

        // Force immediate pose update so frame 0 is applied BEFORE making visible
        this.tensionSpine.state.update(0);
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.alpha = 1;

        // Register this reel and start sound if first
        this._startAnticipationSound();
      } else {
        // Full setup path: clear previous state and start fresh
        this.tensionSpine.state.clearTracks();

        this.tensionSpine.autoUpdate = true;
        this.tensionSpine.state.timeScale = 1;
        this.isVisible = true;

        const animations = this.getAvailableAnimations();

        if (
          animations.includes(this.animNames.in) &&
          animations.includes(this.animNames.idle)
        ) {
          this.tensionSpine.state.setAnimation(0, this.animNames.in, false);
          this.tensionSpine.state.addAnimation(0, this.animNames.idle, true, 0);
        } else if (animations.includes(this.animNames.idle)) {
          this.tensionSpine.state.setAnimation(0, this.animNames.idle, true);
        } else if (animations.length > 0) {
          this.tensionSpine.state.setAnimation(0, animations[0], true);
        }

        // Force immediate pose update so frame 0 of 'in' is applied BEFORE making visible
        this.tensionSpine.state.update(0);
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.alpha = 1;

        // Register this reel and start sound if first
        this._startAnticipationSound();
      }
    } catch (error) {
      this.hideTension();
    }
  }

  /**
   * Play tension exit animation
   */
  playTensionOut() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    const currentAnimation = this.tensionSpine.state.getCurrent(0);

    // If tension_out is already playing, don't restart it
    if (currentAnimation && currentAnimation.animation.name === this.animNames.out) {
      return;
    }

    this.clearAllTimeouts();

    try {
      const animations = this.getAvailableAnimations();

      let animationToPlay = null;

      if (animations.includes(this.animNames.out)) {
        animationToPlay = this.animNames.out;
      } else if (animations.length > 0) {
        animationToPlay = animations[0];
      }

      if (animationToPlay) {
        // Sound keeps playing — stopped by hideTension() after out completes
        this.tensionSpine.state.setAnimation(0, animationToPlay, false);

        const animationDuration =
          this.tensionSpine.skeleton.data.animations.find(
            (anim) => anim.name === animationToPlay
          )?.duration || 1.0;

        const timeoutId = new TickerTimeout(() => {
          this.hideTension();
        }, animationDuration * 1000);
        this.currentTimeouts.push(timeoutId);
      } else {
        this.hideTension();
      }
    } catch (error) {
      this.hideTension();
    }
  }

  /**
   * Update tension spine dimensions for responsive scaling
   * @param {number} newsymbolWidth - New symbol width
   * @param {number} newsymbolHeight - New symbol height
   */
  resizeTension(newsymbolWidth, newsymbolHeight) {
    if (
      newsymbolWidth === this.symbolWidth &&
      newsymbolHeight === this.symbolHeight
    ) {
      return;
    }

    this.symbolWidth = newsymbolWidth;
    this.symbolHeight = newsymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache = null;
    this._lastSymbolWidth = newsymbolWidth;
    this._lastSymbolHeight = newsymbolHeight;

    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        return;
      }

      try {
        this.applyTensionScaling();
      } catch (error) {
        // Fail silently
      }
    });
  }

  /**
   * Apply scaling and positioning to tension spine
   */
  applyTensionScaling() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    const scaleData = this.calculateSpineScale(
      this.tensionSpine,
      this.symbolWidth,
      this.symbolHeight
    );

    if (this.tensionSpine.scale) {
      const baseX = scaleData.scale * this.widthMultiplier;
      const xScale = this.isMirrored ? -baseX : baseX;
      this.tensionSpine.scale.set(xScale, scaleData.scale * this.heightMultiplier);
    }

    if (this.tensionSpine.position) {
      this.tensionSpine.position.set(scaleData.x, scaleData.y);
    }
  }

  /**
   * Create tension spine instance
   */
  createTensionSpine() {
    try {
      if (!this.tensionSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.tensionSpineData.skeleton) ||
        !assets.has(this.tensionSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.tensionSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.tensionSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const tensionSpine = Spine.from({
        skeleton: this.tensionSpineData.skeleton,
        atlas: this.tensionSpineData.atlas,
      });

      if (!tensionSpine || !tensionSpine.skeleton || !tensionSpine.state) {
        return null;
      }

      if (
        !tensionSpine.skeleton.data ||
        !tensionSpine.skeleton.data.animations
      ) {
        return null;
      }

      return tensionSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return [];
    }

    // Check cache first
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    const animations = this.tensionSpine.skeleton.data.animations.map(
      (anim) => anim.name
    );

    // Cache result
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Get tension_out animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   * Cached for performance - invalidated when spine is reinitialized
   */
  getTensionOutDuration() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return 1000; // Fallback to 1 second
    }

    // Check cache first
    if (this._tensionOutDurationCache !== null) {
      return this._tensionOutDurationCache;
    }

    const tensionOutAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === this.animNames.out
    );

    const duration = tensionOutAnim ? tensionOutAnim.duration * 1000 : 1000;

    // Cache result
    this._tensionOutDurationCache = duration;

    return duration;
  }

  /**
   * Get full tension cycle duration (tension_in + tension_idle + tension_out) in milliseconds.
   * Used by ReelControlContext to time how long each tension reel spins.
   * Reads actual spine animation durations for precision — same pattern as useWinController.
   * @returns {number} Duration in milliseconds
   * Cached for performance — invalidated when spine is reinitialized
   */
  getTensionCycleDuration() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return null; // Return null so caller can fall back to config
    }

    // Check cache first
    if (this._tensionCycleDurationCache !== null) {
      return this._tensionCycleDurationCache;
    }

    const animations = this.tensionSpine.skeleton.data.animations;

    const inDuration = (animations.find((a) => a.name === this.animNames.in)?.duration || 0) * 1000;
    const idleDuration = (animations.find((a) => a.name === this.animNames.idle)?.duration || 0) * 1000;
    const outDuration = (animations.find((a) => a.name === this.animNames.out)?.duration || 0) * 1000;
    const idleLoops = FEATURES_CONFIG.TENSION.IDLE_LOOPS ?? 1;

    const cycleDuration = inDuration + (idleDuration * idleLoops) + outDuration;

    // Only cache if we got meaningful data
    if (cycleDuration > 0) {
      this._tensionCycleDurationCache = cycleDuration;
      return cycleDuration;
    }

    return null; // No valid animations found, let caller use fallback
  }

  /**
   * Warm up tension spine with "tension_in" animation
   */
  warmUpTensionSpine() {
    if (!this.tensionSpine || !this.tensionSpine.state) {
      return;
    }

    // Always hide and stop updates — even if animation setup below fails
    this.tensionSpine.alpha = 0.001;
    this.tensionSpine.autoUpdate = false;

    try {
      const animations = this.getAvailableAnimations();

      if (animations.includes(this.animNames.in)) {
        // Set animation to trigger shader compilation and mesh creation
        this.tensionSpine.state.setAnimation(0, this.animNames.in, false);
        this.tensionSpine.state.update(0.016); // Advance one frame
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);

        // Pause at frame 0 — GPU has already compiled everything
        // Do NOT clearTracks here — keeping the animation preserves spine bounds
        this.tensionSpine.state.timeScale = 0;
      }
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Initialize tension spine and add to container
   */
  initializeTensionSpine() {
    this.tensionSpine = this.createTensionSpine();

    if (!this.tensionSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.tensionSpine.skeleton) missingComponents.push("skeleton");
    if (!this.tensionSpine.state) missingComponents.push("state");
    if (!this.tensionSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.tensionSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.tensionSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    this.tensionSpine.position.set(0, 0);
    this.tensionSpine.zIndex = 5;
    this.tensionSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isVisible = false;

    this.reelContainer.addChild(this.tensionSpine);
    this.applyTensionScaling();

    if (this.tensionSpine.parent !== this.reelContainer) {
      return;
    }

    // Invalidate animation caches when spine is initialized
    this._availableAnimationsCache = null;
    this._tensionOutDurationCache = null;
    this._tensionCycleDurationCache = null;

    // Minimal warmup - warm up "tension_in" since that's what plays first
    this.warmUpTensionSpine();

    this.initialized = true;
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.clearAllTimeouts();

    if (this.tensionSpine && !this.tensionSpine.destroyed) {
      try {
        if (this.tensionSpine.state) {
          this.tensionSpine.state.clearTracks();
          this.tensionSpine.state.clearListeners();
        }

        if (this.tensionSpine.parent) {
          this.tensionSpine.parent.removeChild(this.tensionSpine);
        }

        this.tensionSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Fail silently
      }
    }

    this.tensionSpine = null;
    this.reelContainer = null;
    this.tensionSpineData = null;
    this.isVisible = false;
    this.initialized = false;
  }
}

export default TensionReelController;
